import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useCallable, CallStatus } from '../lib/useCallable';
import { useDoc } from '../lib/useFirestore';
import CandidateBanner from '../components/CandidateBanner';
import CandidateFormSettings from './CandidateFormSettings.jsx';
import { usePageIdentity } from '../lib/usePageIdentity';

/**
 * Applications for employment: issuing a link, seeing who has replied, and reading one.
 *
 * The list shows who was asked and whether they have answered, and nothing else. An application
 * holds a home address, ten years of employment history and three named references; that is
 * fetched one application at a time, by somebody who chose to open it, rather than loaded into a
 * page anybody might leave on a screen.
 *
 * ISSUING DEFAULTS TO NOT SENDING. HR said they would paste the link into their own message
 * alongside the conflict-check link, so "Generate the link" is the primary button and emailing is
 * the alternative — one message from a person the applicant has been corresponding with beats two
 * from a system they have never heard of.
 */

const input =
  'rounded border border-slate-300 bg-white px-2 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-900';

const STATUS_LABEL = { invited: 'Invited', in_progress: 'Started', submitted: 'Submitted' };
const STATUS_TONE = {
  invited: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
  in_progress: 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-100',
  submitted: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-100',
};

const when = (value) => {
  if (!value) return '—';
  const date = value.seconds
    ? new Date(value.seconds * 1000)
    : new Date(value._seconds ? value._seconds * 1000 : value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

export default function Applications() {
  usePageIdentity({ title: 'Applications for Employment — AALRR', icon: '/ccc-favicon.svg' });

  const { isAdmin, isFormEditor } = useAuth();
  const { run, busy, error } = useCallable();
  const [params, setParams] = useSearchParams();
  const [applications, setApplications] = useState(null);
  const [scope, setScope] = useState('mine');
  const [issued, setIssued] = useState(null);
  const [copied, setCopied] = useState(false);

  const blankDraft = { applicantEmail: '', applicantName: '', positionDesired: '' };
  const [draft, setDraft] = useState(blankDraft);

  const { data: settings } = useDoc('settings/employmentApplication');
  const hrSet = Boolean(
    settings &&
      (Array.isArray(settings.hrRecipients)
        ? settings.hrRecipients.length
        : String(settings.hrRecipients || '').trim())
  );
  const onDefaultDomain = !String((settings && settings.publicBaseUrl) || '').trim();

  const refresh = useCallback(async () => {
    const result = await run('listApplications', {});
    if (result) {
      setApplications(result.applications || []);
      setScope(result.scope || 'mine');
    }
  }, [run]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const issue = async (sendEmail) => {
    setCopied(false);
    const result = await run('createApplicationLink', { ...draft, sendEmail });
    if (!result) return;
    setIssued(result);
    setDraft(blankDraft);
    refresh();
  };

  const revoke = async (application) => {
    const reason = window.prompt(
      `Switch off the link for ${application.applicantName}?\n\nTheir current link stops working immediately. Anything they have already filled in is kept.\n\nWhy? (optional, recorded in the audit log)`,
      ''
    );
    if (reason === null) return;
    const result = await run('revokeApplicationLink', { formId: application.formId, reason });
    if (result) refresh();
  };

  const reissue = async (application) => {
    const result = await run('reissueApplicationLink', { formId: application.formId, sendEmail: false });
    if (!result) return;
    setIssued(result);
    setCopied(false);
    refresh();
  };

  /**
   * Deleting. Admin only on the server, and confirmed by typing the name.
   *
   * A click-through confirm is too easy for something that destroys a named person's application
   * — their address, their history, their references. Typing the name means reading the row.
   */
  const destroy = async (application) => {
    const typed = window.prompt(
      `Delete ${application.applicantName}'s application permanently?\n\nThis destroys their answers and the stored PDFs. Anything already emailed to HR is unaffected.\n\nType the applicant's name to confirm:`,
      ''
    );
    if (typed === null) return;
    if (typed.trim().toLowerCase() !== application.applicantName.trim().toLowerCase()) {
      window.alert('That name did not match, so nothing was deleted.');
      return;
    }
    const result = await run('deleteApplication', { formId: application.formId });
    if (result) {
      if (params.get('form') === application.formId) setParams({});
      refresh();
    }
  };

  const incomplete = !draft.applicantEmail.trim() || !draft.applicantName.trim();
  const openFormId = params.get('form');

  return (
    <main className="min-h-full bg-slate-100 pb-8 dark:bg-slate-950 sm:pb-12">
      <CandidateBanner title="Applications for employment" showVersion />
      <div className="mx-auto max-w-4xl space-y-5 px-4 pt-8 sm:px-6 sm:pt-12">
        <header className="border-b border-slate-300 pb-5 dark:border-slate-700">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Create a private link to the firm's application for employment. Send it yourself — most
            people paste it into the same email as the conflict-check link — or have the app email
            it. The applicant fills it in without signing in, their answers save as they go, and the
            completed application is emailed to HR as a PDF when they submit it.
          </p>
        </header>

        {!hrSet && (
          <p className="rounded border border-red-400 bg-red-50 p-3 text-sm text-red-900 dark:border-red-600 dark:bg-red-950 dark:text-red-100">
            <strong>No HR recipient is set, so a completed application has nowhere to go.</strong>{' '}
            {isAdmin
              ? 'Set one under Settings below before inviting anyone.'
              : 'Ask an administrator to set one before inviting anyone.'}{' '}
            Links still work and answers are still saved, but nothing will be emailed.
          </p>
        )}

        {isAdmin && (
          <CandidateFormSettings
            settings={settings}
            onDefaultDomain={onDefaultDomain}
            docId="employmentApplication"
            noun="applicant"
          />
        )}
        {/*
          The questions are edited on the form itself now, at /apply/form: the application as an
          applicant sees it, with an Edit switch that makes every part of it changeable in place.
          (The candidate page keeps CandidateFormBuilder.)
        */}
        {isFormEditor ? (
          <section className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">The application form</h3>
              <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-300">
                See it exactly as applicants do, and change any part of it: questions, help text,
                sections, the order, what is required, and the wording at the top and on the button.
              </p>
            </div>
            <Link
              to="/apply/form"
              className="rounded bg-firm-700 px-4 py-2 text-sm font-medium text-white hover:bg-firm-900 dark:bg-firm-500"
            >
              View and edit the form
            </Link>
          </section>
        ) : null}

        <section className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Invite an applicant</h3>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <label className="text-xs text-slate-600 dark:text-slate-300">
              Applicant email
              <input
                className={`${input} mt-1 w-full`}
                value={draft.applicantEmail}
                placeholder="name@example.com"
                onChange={(e) => setDraft({ ...draft, applicantEmail: e.target.value })}
              />
            </label>
            <label className="text-xs text-slate-600 dark:text-slate-300">
              Applicant name
              <input
                className={`${input} mt-1 w-full`}
                value={draft.applicantName}
                onChange={(e) => setDraft({ ...draft, applicantName: e.target.value })}
              />
            </label>
            <label className="text-xs text-slate-600 dark:text-slate-300 sm:col-span-2">
              Position applied for
              <input
                className={`${input} mt-1 w-full`}
                value={draft.positionDesired}
                placeholder="Legal Assistant"
                onChange={(e) => setDraft({ ...draft, positionDesired: e.target.value })}
              />
              <span className="mt-1 block text-slate-500 dark:text-slate-400">
                Optional. Pre-fills the applicant's form; they can still change it.
              </span>
            </label>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => issue(false)}
              disabled={busy || incomplete}
              className="rounded bg-firm-700 px-4 py-2 text-sm font-medium text-white hover:bg-firm-900 disabled:opacity-50 dark:bg-firm-500 dark:hover:bg-firm-400"
            >
              Generate the link
            </button>
            <button
              type="button"
              onClick={() => issue(true)}
              disabled={busy || incomplete}
              className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:border-firm-500 disabled:opacity-50 dark:border-slate-600 dark:text-slate-200"
            >
              Generate and email it
            </button>
            <CallStatus busy={busy} error={error} />
          </div>

          {issued && (
            <div className="mt-3 rounded border border-emerald-300 bg-emerald-50 p-3 text-sm dark:border-emerald-700 dark:bg-emerald-950">
              <p className="font-medium text-emerald-900 dark:text-emerald-100">
                {issued.emailed ? 'Link created and emailed.' : 'Link created.'}
              </p>
              <p className="mt-1 break-all font-mono text-xs text-emerald-900 dark:text-emerald-100">
                {issued.url}
              </p>
              <div className="mt-2 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(issued.url);
                    setCopied(true);
                  }}
                  className="rounded border border-emerald-400 px-3 py-1 text-xs text-emerald-900 dark:border-emerald-600 dark:text-emerald-100"
                >
                  {copied ? 'Copied' : 'Copy link'}
                </button>
                {/* Said once, plainly: this link is the credential. */}
                <span className="text-xs text-emerald-800 dark:text-emerald-200">
                  Anyone holding this link can complete the application, so send it only to the
                  applicant.
                </span>
              </div>
            </div>
          )}
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
              {scope === 'all' ? 'All applications' : 'Applications you sent'}{' '}
              <span className="ml-1 text-xs font-normal text-slate-500 dark:text-slate-400">
                {applications ? applications.length : ''}
              </span>
            </h3>
            <button type="button" onClick={refresh} className="text-xs text-firm-700 underline dark:text-firm-300">
              Refresh
            </button>
          </div>

          {applications === null ? (
            <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">Loading…</p>
          ) : applications.length === 0 ? (
            <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
              Nothing yet. Invite somebody above.
            </p>
          ) : (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[820px] text-left text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  <tr>
                    <th className="py-1">Applicant</th>
                    <th className="py-1">Position</th>
                    <th className="py-1">Status</th>
                    <th className="py-1">Invited</th>
                    <th className="py-1">Submitted</th>
                    <th className="w-px py-1" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {applications.map((application) => (
                    <tr key={application.formId}>
                      <td className="py-1.5 align-top">
                        <div className="font-medium text-ink dark:text-slate-100">
                          {application.applicantName}
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">
                          {application.applicantEmail}
                        </div>
                      </td>
                      <td className="py-1.5 align-top text-slate-700 dark:text-slate-200">
                        {application.positionDesired || '—'}
                      </td>
                      <td className="py-1.5 align-top">
                        <span
                          className={`inline-block rounded px-2 py-0.5 text-xs ${
                            STATUS_TONE[application.status] || STATUS_TONE.invited
                          }`}
                        >
                          {STATUS_LABEL[application.status] || application.status}
                        </span>
                        {!application.linkLive && (
                          <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">link off</span>
                        )}
                      </td>
                      <td className="py-1.5 align-top text-xs text-slate-600 dark:text-slate-300">
                        {when(application.invitedAt)}
                      </td>
                      <td className="py-1.5 align-top text-xs text-slate-600 dark:text-slate-300">
                        {when(application.submittedAt)}
                      </td>
                      {/* One line, never wrapped: with four actions, Delete used to break onto a
                          line of its own where it read as belonging to the next row. */}
                      <td className="w-px py-1.5 text-right align-top">
                        <div className="flex flex-nowrap items-center justify-end gap-3 whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => setParams({ form: application.formId })}
                            className="text-xs text-firm-700 underline dark:text-firm-300"
                          >
                            Open
                          </button>
                          {application.status !== 'submitted' && (
                            <button
                              type="button"
                              onClick={() => reissue(application)}
                              className="text-xs text-firm-700 underline dark:text-firm-300"
                            >
                              New link
                            </button>
                          )}
                          {application.linkLive && (
                            <button
                              type="button"
                              onClick={() => revoke(application)}
                              className="text-xs text-amber-700 underline dark:text-amber-300"
                            >
                              Switch off
                            </button>
                          )}
                          {isAdmin && (
                            <button
                              type="button"
                              onClick={() => destroy(application)}
                              className="text-xs text-red-600 underline dark:text-red-400"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {openFormId && <ApplicationDetail formId={openFormId} onClose={() => setParams({})} />}
      </div>
    </main>
  );
}

/** One application, fetched only when somebody chose to open it. */
function ApplicationDetail({ formId, onClose }) {
  const { run, busy, error } = useCallable();
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await run('getApplication', { formId });
      if (!cancelled) setData(result);
    })();
    return () => {
      cancelled = true;
    };
  }, [formId, run]);

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
          {data ? data.application.applicantName : 'Application'}
        </h3>
        <button type="button" onClick={onClose} className="text-xs text-slate-500 underline dark:text-slate-400">
          Close
        </button>
      </div>

      {busy && !data ? (
        <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">Loading…</p>
      ) : error ? (
        <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>
      ) : data ? (
        <div className="mt-3 space-y-3 text-sm">
          <p className="text-slate-600 dark:text-slate-300">
            {data.application.positionDesired || 'No position given'} ·{' '}
            {data.application.applicantEmail} · invited by {data.application.invitedByName || 'somebody'}
          </p>

          {data.documents && data.documents.length > 0 ? (
            <div className="space-y-2">
              {data.documents.map((document) => (
                <div key={document.filename}>
                  <a
                    href={document.url}
                    className="inline-block rounded bg-firm-700 px-4 py-2 text-sm font-medium text-white hover:bg-firm-900 dark:bg-firm-500 dark:hover:bg-firm-400"
                  >
                    {document.separate ? 'Open the self-identification form' : 'Open the application'}
                  </a>
                  {/*
                    Labelled everywhere it appears, not only inside the PDF. Somebody downloading
                    this needs to know where it goes BEFORE they save it, not after.
                  */}
                  {document.separate && (
                    <p className="mt-1 text-xs text-amber-800 dark:text-amber-300">
                      Voluntary, and to be retained apart from the personnel file.
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 dark:text-slate-400">
              {data.application.status === 'submitted'
                ? 'No documents were stored for this application.'
                : 'Not submitted yet, so there is nothing to read.'}
            </p>
          )}

          {data.mailProblem && (
            <p className="rounded border border-red-400 bg-red-50 p-2 text-xs text-red-900 dark:border-red-600 dark:bg-red-950 dark:text-red-100">
              {data.mailProblem}
            </p>
          )}
        </div>
      ) : null}
    </section>
  );
}
